"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AnalyticsEvent {
  event_type: string;
  page_path: string;
  session_id: string;
  timestamp: number;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  payload?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const FLUSH_INTERVAL_MS = 5_000;
const BATCH_SIZE_LIMIT = 20;
const INGEST_ENDPOINT = "/api/analytics/ingest";
const RAGE_CLICK_WINDOW_MS = 2_000;
const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_RADIUS_PX = 30;

/* ------------------------------------------------------------------ */
/*  Utility: Session ID                                                */
/* ------------------------------------------------------------------ */

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let sid = sessionStorage.getItem("_iv_sid");
  if (!sid) {
    sid =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem("_iv_sid", sid);
  }
  return sid;
}

/* ------------------------------------------------------------------ */
/*  Utility: UTM Parameters                                            */
/* ------------------------------------------------------------------ */

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

function parseAndStoreUTM(searchParams: URLSearchParams | null): UTMParams {
  if (typeof window === "undefined") return {};

  // If URL has UTM params, store them in sessionStorage
  if (searchParams) {
    const source = searchParams.get("utm_source");
    const medium = searchParams.get("utm_medium");
    const campaign = searchParams.get("utm_campaign");
    if (source) sessionStorage.setItem("_iv_utm_source", source);
    if (medium) sessionStorage.setItem("_iv_utm_medium", medium);
    if (campaign) sessionStorage.setItem("_iv_utm_campaign", campaign);
  }

  return {
    utm_source: sessionStorage.getItem("_iv_utm_source") || undefined,
    utm_medium: sessionStorage.getItem("_iv_utm_medium") || undefined,
    utm_campaign: sessionStorage.getItem("_iv_utm_campaign") || undefined,
  };
}

/* ------------------------------------------------------------------ */
/*  Utility: Referrer Detection                                        */
/* ------------------------------------------------------------------ */

function detectReferrerSource(): string | undefined {
  if (typeof document === "undefined" || !document.referrer) return undefined;
  const ref = document.referrer.toLowerCase();
  if (ref.includes("instagram.com") || ref.includes("l.instagram.com"))
    return "instagram";
  if (ref.includes("whatsapp") || ref.includes("wa.me")) return "whatsapp";
  if (ref.includes("google")) return "google";
  if (ref.includes("facebook") || ref.includes("fb.com")) return "facebook";
  try {
    return new URL(document.referrer).hostname;
  } catch {
    return document.referrer.slice(0, 100);
  }
}

/* ------------------------------------------------------------------ */
/*  Utility: Device / Browser / OS Detection                           */
/* ------------------------------------------------------------------ */

function detectDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w <= 1024) return "tablet";
  return "desktop";
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome") && !ua.includes("Edg/")) return "Chrome";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  return "Other";
}

function detectOS(): string {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "Android";
  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (/Win/.test(ua)) return "Windows";
  if (/Mac/.test(ua)) return "Mac";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}

/* ------------------------------------------------------------------ */
/*  Utility: Flush helpers                                             */
/* ------------------------------------------------------------------ */

function flushViaFetch(events: AnalyticsEvent[]): void {
  if (events.length === 0) return;
  fetch(INGEST_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {
    /* silently drop — analytics should never break the site */
  });
}

function flushViaBeacon(events: AnalyticsEvent[]): void {
  if (events.length === 0) return;
  const blob = new Blob([JSON.stringify({ events })], {
    type: "application/json",
  });
  navigator.sendBeacon(INGEST_ENDPOINT, blob);
}

/* ================================================================== */
/*  Tracker Component                                                  */
/* ================================================================== */

export default function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Refs survive re-renders without triggering them
  const queueRef = useRef<AnalyticsEvent[]>([]);
  const sessionIdRef = useRef<string>("");
  const visibleTimeRef = useRef<number>(0);
  const visibleSinceRef = useRef<number>(Date.now());
  const scrollThresholdsRef = useRef<Set<number>>(new Set());
  const pageLoadTimeRef = useRef<number>(Date.now());
  const recentClicksRef = useRef<{ x: number; y: number; t: number }[]>([]);
  const isFirstPageviewRef = useRef<boolean>(true);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ---------------------------------------------------------------- */
  /*  Skip dashboard paths                                             */
  /* ---------------------------------------------------------------- */
  const isDashboard = pathname.startsWith("/dashboard");

  /* ---------------------------------------------------------------- */
  /*  Defer initialization until page is idle                          */
  /* ---------------------------------------------------------------- */
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isDashboard) return;
    // Embedded previews (homepage/template-page iframes) don't count as
    // visits — same guard as Analytics.tsx and PostHogProvider.
    if (
      window.self !== window.top ||
      window.location.search.includes("embed=true")
    )
      return;
    const cb = () => setIsReady(true);
    if (typeof requestIdleCallback !== "undefined") {
      const id = requestIdleCallback(cb, { timeout: 2000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(cb, 1500);
      return () => clearTimeout(id);
    }
  }, [isDashboard]);

  /* ---------------------------------------------------------------- */
  /*  Enqueue an event                                                 */
  /* ---------------------------------------------------------------- */
  const enqueue = useCallback(
    (
      eventType: string,
      payload?: Record<string, unknown>,
      extra?: Partial<AnalyticsEvent>
    ) => {
      const evt: AnalyticsEvent = {
        event_type: eventType,
        page_path: pathname,
        session_id: sessionIdRef.current,
        timestamp: Date.now(),
        ...extra,
      };
      if (payload) evt.payload = payload;
      queueRef.current.push(evt);

      // Auto-flush when batch is full
      if (queueRef.current.length >= BATCH_SIZE_LIMIT) {
        const batch = queueRef.current.splice(0);
        flushViaFetch(batch);
      }
    },
    [pathname]
  );

  /* ---------------------------------------------------------------- */
  /*  Flush remaining events                                           */
  /* ---------------------------------------------------------------- */
  const flush = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const batch = queueRef.current.splice(0);
    flushViaFetch(batch);
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Setup: session ID + flush timer                                  */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    sessionIdRef.current = getSessionId();

    // Periodic flush
    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    };
  }, [isReady, isDashboard, flush]);

  /* ---------------------------------------------------------------- */
  /*  Page View tracking                                               */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    // Reset per-page state
    scrollThresholdsRef.current = new Set();
    pageLoadTimeRef.current = Date.now();
    visibleTimeRef.current = 0;
    visibleSinceRef.current = Date.now();

    const utmParams = parseAndStoreUTM(searchParams);
    const referrerSource = detectReferrerSource();

    const extra: Partial<AnalyticsEvent> = {
      referrer: document.referrer || undefined,
      utm_source: utmParams.utm_source || referrerSource,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
    };

    // Include device info on first pageview
    if (isFirstPageviewRef.current) {
      extra.device_type = detectDeviceType();
      extra.browser = detectBrowser();
      extra.os = detectOS();
      isFirstPageviewRef.current = false;
    }

    enqueue("pageview", undefined, extra);
  }, [isReady, pathname, searchParams, isDashboard, enqueue]);

  /* ---------------------------------------------------------------- */
  /*  Click tracking + Rage Click detection                            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const scrollW = document.documentElement.scrollWidth || 1;
      const scrollH = document.documentElement.scrollHeight || 1;
      const xPct = ((e.pageX / scrollW) * 100).toFixed(2);
      const yPct = ((e.pageY / scrollH) * 100).toFixed(2);

      enqueue("click", {
        x_pct: xPct,
        y_pct: yPct,
        element_tag: target.tagName,
        element_id: target.id || undefined,
        element_class: (target.className || "").toString().slice(0, 100) || undefined,
        element_text: (target.textContent || "").slice(0, 50) || undefined,
        viewport_w: window.innerWidth,
        viewport_h: window.innerHeight,
      });

      // --- Rage click detection ---
      const now = Date.now();
      const px = e.pageX;
      const py = e.pageY;

      // Prune old clicks
      recentClicksRef.current = recentClicksRef.current.filter(
        (c) => now - c.t < RAGE_CLICK_WINDOW_MS
      );
      recentClicksRef.current.push({ x: px, y: py, t: now });

      // Count nearby clicks
      const nearby = recentClicksRef.current.filter(
        (c) =>
          Math.abs(c.x - px) <= RAGE_CLICK_RADIUS_PX &&
          Math.abs(c.y - py) <= RAGE_CLICK_RADIUS_PX
      );

      if (nearby.length >= RAGE_CLICK_THRESHOLD) {
        enqueue("rage_click", {
          x_pct: xPct,
          y_pct: yPct,
          element_tag: target.tagName,
          click_count: nearby.length,
        });
        // Reset to avoid spamming
        recentClicksRef.current = [];
      }
    };

    document.addEventListener("click", handleClick, { passive: true });
    return () => document.removeEventListener("click", handleClick);
  }, [isReady, isDashboard, enqueue]);

  /* ---------------------------------------------------------------- */
  /*  Scroll Depth tracking                                            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    const handleScroll = () => {
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      if (scrollHeight <= 0) return;

      const pct = (scrollTop / scrollHeight) * 100;
      const thresholds = [25, 50, 75, 100];

      for (const t of thresholds) {
        if (pct >= t && !scrollThresholdsRef.current.has(t)) {
          scrollThresholdsRef.current.add(t);
          enqueue("scroll", { depth: t });
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isReady, isDashboard, enqueue]);

  /* ---------------------------------------------------------------- */
  /*  Visibility / Time on Page tracking                               */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Accumulate visible time
        visibleTimeRef.current += Date.now() - visibleSinceRef.current;
      } else {
        // Page became visible again
        visibleSinceRef.current = Date.now();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isReady, isDashboard]);

  /* ---------------------------------------------------------------- */
  /*  Section Visibility tracking (IntersectionObserver)               */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;
    if (isDashboard) return;

    const seenSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const sectionId =
            el.getAttribute("data-section") || el.id || undefined;
          if (!sectionId || seenSections.has(sectionId)) continue;

          seenSections.add(sectionId);
          enqueue("section_visible", {
            section_id: sectionId,
            time_since_load_ms: Date.now() - pageLoadTimeRef.current,
          });
        }
      },
      { threshold: 0.3 }
    );

    // Schedule observation via requestIdleCallback for non-blocking setup
    const schedule =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 100);

    schedule(() => {
      // Elements with data-section attribute
      document
        .querySelectorAll("[data-section]")
        .forEach((el) => observer.observe(el));
      // Section elements with id
      document
        .querySelectorAll("section[id]")
        .forEach((el) => observer.observe(el));
    });

    return () => observer.disconnect();
  }, [isReady, isDashboard, pathname, enqueue]);

  /* ---------------------------------------------------------------- */
  /*  Page Unload — send session_end + flush remaining events          */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (isDashboard) return;

    const handleUnload = () => {
      // Calculate final visible time
      if (document.visibilityState === "visible") {
        visibleTimeRef.current += Date.now() - visibleSinceRef.current;
      }

      // Enqueue session_end
      queueRef.current.push({
        event_type: "session_end",
        page_path: pathname,
        session_id: sessionIdRef.current,
        timestamp: Date.now(),
        payload: { duration_ms: visibleTimeRef.current },
      });

      // Flush everything via beacon (most reliable on unload)
      flushViaBeacon(queueRef.current.splice(0));
    };

    window.addEventListener("beforeunload", handleUnload);
    // Also handle pagehide for Safari/iOS
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [isDashboard, pathname]);

  /* ---------------------------------------------------------------- */
  /*  Render — nothing visible. Session recording handled by PostHog  */
  /* ---------------------------------------------------------------- */
  return null;
}
